import React, { useEffect, useState, useRef } from 'react'
import { useBrickStore } from '@store/brickStore'
import './StatusBar.css'

/**
 * StatusBar Component
 *
 * Displays build statistics at bottom-right (desktop) or bottom-center (mobile)
 *
 * Features:
 * - Brick count: Total number of placed bricks
 * - FPS counter: Real-time frame rate using requestAnimationFrame
 * - Selection status: Shows when a brick is selected (Task 3.2.1)
 * - Keyboard shortcuts hint: Press Delete to remove selected brick
 * - Responsive positioning (bottom-right desktop, bottom-center mobile)
 * - ARIA labels for accessibility
 *
 * Performance:
 * - FPS calculated using requestAnimationFrame for accurate timing
 * - Updates smoothed over 60 frames for stable display
 * - Cleanup on unmount to prevent memory leaks
 *
 * @component
 */
export const StatusBar: React.FC = () => {
  // Get brick count from store
  const brickCount = useBrickStore((state) => state.bricks.size)
  const selectedBrickId = useBrickStore((state) => state.selectedBrickId) // Task 3.2.1

  // FPS tracking state
  const [fps, setFps] = useState(60)
  const frameCountRef = useRef(0)
  const lastTimeRef = useRef(performance.now())
  const animationFrameRef = useRef<number>()

  // Calculate FPS using requestAnimationFrame
  useEffect(() => {
    const calculateFPS = () => {
      const currentTime = performance.now()
      frameCountRef.current++

      // Update FPS every second (1000ms)
      const deltaTime = currentTime - lastTimeRef.current
      if (deltaTime >= 1000) {
        const currentFps = Math.round(
          (frameCountRef.current * 1000) / deltaTime
        )
        setFps(currentFps)
        frameCountRef.current = 0
        lastTimeRef.current = currentTime
      }

      // Continue loop
      animationFrameRef.current = requestAnimationFrame(calculateFPS)
    }

    // Start FPS calculation loop
    animationFrameRef.current = requestAnimationFrame(calculateFPS)

    // Cleanup on unmount
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [])

  return (
    <div className="status-bar" role="status" aria-live="polite">
      <div className="status-bar__container">
        <div
          className="status-bar__item"
          aria-label={`${brickCount} bricks placed`}
        >
          <span className="status-bar__label">Bricks:</span>
          <span className="status-bar__value">{brickCount}</span>
        </div>

        <div className="status-bar__separator" aria-hidden="true" />

        <div
          className="status-bar__item"
          aria-label={`Running at ${fps} frames per second`}
        >
          <span className="status-bar__label">FPS:</span>
          <span className="status-bar__value">{fps}</span>
        </div>

        {/* Selection indicator (Task 3.2.1) */}
        {selectedBrickId && (
          <>
            <div className="status-bar__separator" aria-hidden="true" />
            <div
              className="status-bar__item status-bar__item--selected"
              aria-label="Brick selected. Press Delete to remove."
            >
              <span className="status-bar__label">Selected</span>
              <span className="status-bar__value status-bar__hint">
                Press Del to delete
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
